/**
 * Hareket formu. `TransactionType`'ın TAMAMINI destekler ve alanları türe göre
 * gösterir/gizler — kural `lib/txShape.ts` içindeki tek tabloda.
 *
 * Nakit türlerinde tek "Tutar" alanı gösterilir ve kayda
 * `quantity = "1"`, `price = <tutar>` olarak yazılır (bkz. txShape.ts'deki
 * sözleşme boşluğu notu).
 */

import { useMemo, useState } from "react";
import type { Account, NewTransaction } from "@parafon/contracts/local-api";
import type { AssetBinding, Transaction, TransactionType, VergiKaynagi } from "@parafon/contracts";
import { isKnownCurrency } from "@parafon/contracts";
import { M } from "../dil";
import { TRANSACTION_TYPES, TRANSACTION_TYPE_LABEL, todayIso } from "../lib/format";
import { newId } from "../lib/id";
import { TX_SHAPE, cashLabel } from "../lib/txShape";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useMutation } from "../repo/useAsync";
import { DecimalInput, readDecimalField } from "../ui/DecimalInput";
import { Button, DateInput, Field, InlineError, Notice, Select, TextArea, TextInput } from "../ui/primitives";
import { satisNetTutari } from "../lib/decimal";
import { AssetBindingField } from "./AssetBindingField";
import { CurrencySelect } from "../ui/CurrencySelect";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { varlikSiniriEngeli } from "../kabiliyet/hesapSinirlari";

export interface TransactionFormProps {
  readonly accounts: readonly Account[];
  readonly transaction: Transaction | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

interface FieldErrors {
  accountId?: string;
  tradeDate?: string;
  settleDate?: string;
  amount?: string;
  quantity?: string;
  price?: string;
  fee?: string;
  tax?: string;
  fx?: string;
  currency?: string;
  instrumentId?: string;
  label?: string;
  assetCurrency?: string;
  vergiYili?: string;
  alinanTutar?: string;
  alinanParaBirimi?: string;
}

export function TransactionForm({ accounts, transaction, onDone, onCancel }: TransactionFormProps) {
  const { repo } = useRepositoryContext();
  const save = useMutation();
  // Gelir modeli: hesap başına varlık sınırı (yeni varlık alımı; satış serbest).
  const { hesapBasinaVarlik } = useSinirlar();
  const [kilit, setKilit] = useState<string | null>(null);

  const firstAccount = accounts[0];
  const [accountId, setAccountId] = useState(transaction?.accountId ?? firstAccount?.id ?? "");
  const [type, setType] = useState<TransactionType>(transaction?.type ?? "BUY");
  const [tradeDate, setTradeDate] = useState(transaction?.tradeDate ?? todayIso());
  const [settleDate, setSettleDate] = useState(transaction?.settleDate ?? "");
  /**
   * YENİ KAYIT TANINMAYAN BİR KODLA BAŞLAMAZ.
   *
   * Varsayılan hesabın para biriminden geliyor; kapalı liste gelmeden önce
   * açılmış bir hesapta o kod "TL" olabilir ve yeni hareket formu onu ÖN
   * SEÇİLİ gösteriyordu. Kapının kapalı olmasının anlamı bu: yanlış kod
   * yeni bir kayda giremez — miras yoluyla da giremez.
   *
   * Yerine TRY KOYULMUYOR: kullanıcının hesabı hangi para biriminde bilmiyoruz
   * ve tahmin etmek, sessizce yanlış kaydetmenin başka bir yolu olurdu. Alan
   * BOŞ başlıyor, kullanıcı seçiyor, seçmeden kaydedemiyor.
   *
   * DÜZENLEMEDE kaydın kendi kodu korunuyor (tanınmasa bile): oradaki soru
   * "ne yazmalı" değil, "ne yazılmış" — ve onu değiştirmek kullanıcının işi.
   */
  const devralinan = transaction?.currency ?? firstAccount?.currency ?? "TRY";
  const [currency, setCurrency] = useState(
    transaction !== null || isKnownCurrency(devralinan) ? devralinan : "",
  );

  const shape = TX_SHAPE[type];

  // Nakit türlerinde kayıttaki `price` alanı tutarı taşır (bkz. txShape.ts).
  const initialAmount = transaction !== null && TX_SHAPE[transaction.type].amount ? transaction.price : "";
  const initialQuantity = transaction !== null && !TX_SHAPE[transaction.type].amount ? transaction.quantity : "";
  const initialPrice = transaction !== null && !TX_SHAPE[transaction.type].amount ? transaction.price : "";

  const [amount, setAmount] = useState(initialAmount);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [price, setPrice] = useState(initialPrice);
  const [fee, setFee] = useState(transaction?.fee ?? "");
  const [tax, setTax] = useState(transaction?.tax ?? "");
  const [fx, setFx] = useState(transaction?.fxRateOverride ?? "");
  const [note, setNote] = useState(transaction?.note ?? "");
  // Geri yatırılan temettü (19.10.2026): alışın tamamı temettüyle yapıldı.
  const [temettuden, setTemettuden] = useState(transaction?.temettuden === true);
  // Vergi ödemesi (19.10.2026): verginin yılı ve ödemenin kaynağı.
  const [vergiYili, setVergiYili] = useState(
    transaction?.vergiYili !== undefined ? String(transaction.vergiYili) : String(new Date().getFullYear() - 1),
  );
  const [vergiKaynagi, setVergiKaynagi] = useState<VergiKaynagi>(transaction?.vergiKaynagi ?? "nakit");
  // Döviz alış/satış (22.10.2026): alınan taraf.
  const [alinanTutar, setAlinanTutar] = useState(transaction?.alinanTutar ?? "");
  const [alinanParaBirimi, setAlinanParaBirimi] = useState(transaction?.alinanParaBirimi ?? "");
  // "Parayı çektim" (19.10.2026): yalnızca YENİ satışta; aynı gün net tutarda çekme yazılır.
  const [paraCekildi, setParaCekildi] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const [asset, setAsset] = useState<AssetBinding>(
    transaction?.asset ?? { kind: "tracked", instrumentId: "" },
  );
  const [assetTouched, setAssetTouched] = useState(transaction !== null);

  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: `${a.name} (${a.currency})` })),
    [accounts],
  );

  const onTypeChange = (next: TransactionType): void => {
    setType(next);
    setErrors({});
    // Nakit türüne geçildiğinde varlık alanı nakit varsayılanına döner —
    // kullanıcı elle değiştirmediyse.
    if (TX_SHAPE[next].assetMode === "cash" && !assetTouched) {
      setAsset({ kind: "manual", label: cashLabel(currency), currency });
    }
  };

  /** Hesap değişince para birimi ONUN kodundan gelir — tanınıyorsa. */
  const onAccountChange = (next: string): void => {
    setAccountId(next);
    if (transaction !== null) return;
    const hesap = accounts.find((a) => a.id === next);
    if (hesap === undefined) return;
    onCurrencyChange(isKnownCurrency(hesap.currency) ? hesap.currency : "");
  };

  const onCurrencyChange = (next: string): void => {
    setCurrency(next);
    if (shape.assetMode === "cash" && !assetTouched) {
      setAsset({ kind: "manual", label: cashLabel(next.toUpperCase()), currency: next.toUpperCase() });
    }
  };

  const submit = async (): Promise<void> => {
    const next: FieldErrors = {};

    if (accountId === "") next.accountId = M.hareketler.form.hesapSecin;
    if (tradeDate === "") next.tradeDate = M.hareketler.form.tarihZorunlu;
    if (shape.settleDate && settleDate !== "" && settleDate < tradeDate) {
      next.settleDate = M.hareketler.form.valorOnce;
    }
    if (!isKnownCurrency(currency)) next.currency = M.hareketler.form.paraBirimiSecin;

    if (asset.kind === "tracked" && asset.instrumentId.trim() === "") next.instrumentId = M.hareketler.form.kimlikZorunlu;
    if (asset.kind === "manual") {
      if (asset.label.trim() === "") next.label = M.hareketler.form.etiketZorunlu;
      if (!isKnownCurrency(asset.currency)) next.assetCurrency = M.hareketler.form.paraBirimiSecin;
    }

    let quantityOut = "1";
    let priceOut = "0";

    if (shape.amount) {
      const r = readDecimalField(amount, { required: true });
      if (!r.ok) next.amount = r.message;
      else priceOut = r.value ?? "0";
    } else {
      if (shape.quantity) {
        const r = readDecimalField(quantity, { required: true });
        if (!r.ok) next.quantity = r.message;
        else quantityOut = r.value ?? "0";
      }
      if (shape.price) {
        const r = readDecimalField(price, { required: shape.priceRequired });
        if (!r.ok) next.price = r.message;
        else priceOut = r.value ?? "0";
      }
    }

    const feeRead = readDecimalField(fee, { required: false });
    if (!feeRead.ok) next.fee = feeRead.message;
    const taxRead = readDecimalField(tax, { required: false });
    if (!taxRead.ok) next.tax = taxRead.message;
    const fxRead = readDecimalField(fx, { required: false });
    if (!fxRead.ok) next.fx = fxRead.message;

    let yilOut: number | null = null;
    if (type === "TAX_PAYMENT") {
      const y = vergiYili.trim();
      if (!/^\d{4}$/.test(y)) next.vergiYili = M.hareketler.form.vergiYili;
      else yilOut = Number(y);
    }

    let alinanOut: string | null = null;
    if (type === "FX_CONVERSION") {
      const r = readDecimalField(alinanTutar, { required: true });
      if (!r.ok) next.alinanTutar = r.message;
      else alinanOut = r.value ?? null;
      if (!isKnownCurrency(alinanParaBirimi)) next.alinanParaBirimi = M.hareketler.form.paraBirimiSecin;
      else if (alinanParaBirimi.toUpperCase() === currency.trim().toUpperCase()) {
        next.alinanParaBirimi = M.hareketler.form.alinanFarkli;
      }
    }

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const cleanedAsset: AssetBinding =
      asset.kind === "tracked"
        ? { kind: "tracked", instrumentId: asset.instrumentId.trim() }
        : { kind: "manual", label: asset.label.trim(), currency: asset.currency.trim().toUpperCase() };

    const input: NewTransaction = {
      id: transaction?.id ?? newId(),
      accountId,
      asset: cleanedAsset,
      type,
      tradeDate,
      quantity: quantityOut,
      price: priceOut,
      currency: currency.trim().toUpperCase(),
      ...(shape.settleDate && settleDate !== "" ? { settleDate } : {}),
      ...(shape.fee && feeRead.ok && feeRead.value !== undefined ? { fee: feeRead.value } : {}),
      ...(shape.tax && taxRead.ok && taxRead.value !== undefined ? { tax: taxRead.value } : {}),
      ...(shape.fxOverride && fxRead.ok && fxRead.value !== undefined ? { fxRateOverride: fxRead.value } : {}),
      ...(note.trim() === "" ? {} : { note: note.trim() }),
      // Her zaman yazılır: işaretli bir alış başka türe çevrilirse işaret düşsün.
      temettuden: type === "BUY" && temettuden,
      ...(type === "TAX_PAYMENT" && yilOut !== null ? { vergiYili: yilOut, vergiKaynagi } : {}),
      ...(type === "FX_CONVERSION" && alinanOut !== null
        ? { alinanTutar: alinanOut, alinanParaBirimi: alinanParaBirimi.toUpperCase() }
        : {}),
    };

    // Satış parası dışarı alındı: aynı gün, aynı hesap ve para biriminde,
    // satışın NET tutarında (brüt − masraf − vergi) bir çekme.
    const cekme: NewTransaction | null =
      transaction === null && type === "SELL" && paraCekildi
        ? (() => {
            const tutar = satisNetTutari(input.quantity, input.price, input.fee, input.tax);
            return tutar === null
              ? null
              : {
                  id: newId(),
                  accountId,
                  asset: { kind: "manual", label: cashLabel(input.currency), currency: input.currency },
                  type: "WITHDRAWAL",
                  tradeDate,
                  quantity: "1",
                  price: tutar,
                  currency: input.currency,
                  note: M.hareketler.form.cekmeNotu,
                };
          })()
        : null;

    const engel = await varlikSiniriEngeli(repo, hesapBasinaVarlik, input, transaction?.id ?? null);
    setKilit(engel);
    if (engel !== null) return;

    const ok = await save.run(async () => {
      if (transaction === null) {
        if (cekme !== null) await repo.transactions.createMany([input, cekme]);
        else await repo.transactions.create(input);
      } else await repo.transactions.update(transaction.id, input);
    });
    if (ok) onDone();
  };

  const err = (key: keyof FieldErrors): { error?: string } => {
    const v = errors[key];
    return v === undefined ? {} : { error: v };
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-3"
      noValidate
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={M.hareketler.form.hesap} htmlFor="tx-account" required {...err("accountId")}>
          <Select
            id="tx-account"
            value={accountId}
            onChange={(e) => onAccountChange(e.currentTarget.value)}
            options={accountOptions}
            {...(accounts.length === 0 ? { placeholder: M.hareketler.form.onceHesap } : {})}
            aria-invalid={errors.accountId !== undefined}
          />
        </Field>

        <Field label={M.hareketler.form.hareketTuru} htmlFor="tx-type" required>
          <Select
            id="tx-type"
            value={type}
            onChange={(e) => onTypeChange(e.currentTarget.value as TransactionType)}
            options={TRANSACTION_TYPES.map((t) => ({ value: t, label: TRANSACTION_TYPE_LABEL[t] }))}
          />
        </Field>

        <Field label={M.hareketler.form.paraBirimi} htmlFor="tx-currency" required {...err("currency")}>
          <CurrencySelect
            id="tx-currency"
            className="pf-fig"
            value={currency}
            onChange={onCurrencyChange}
            invalid={errors.currency !== undefined}
          />
        </Field>

        <Field label={M.hareketler.form.islemTarihi} htmlFor="tx-date" required {...err("tradeDate")}>
          <DateInput
            id="tx-date"
            value={tradeDate}
            onChange={(e) => setTradeDate(e.currentTarget.value)}
            aria-invalid={errors.tradeDate !== undefined}
          />
        </Field>

        {shape.settleDate && (
          <Field label={M.hareketler.form.valor} htmlFor="tx-settle" hint={
              shape.amount
                ? M.hareketler.form.valorNakit
                : M.hareketler.form.valorTakas
            } {...err("settleDate")}>
            <DateInput id="tx-settle" value={settleDate} onChange={(e) => setSettleDate(e.currentTarget.value)} />
          </Field>
        )}
      </div>

      <p className="text-[12px] text-faint">{shape.hint}</p>

      <div className="grid gap-3 sm:grid-cols-3">
        {shape.amount && (
          <DecimalInput
            label={shape.quantityLabel}
            value={amount}
            onChange={setAmount}
            required
            currency={currency}
            {...err("amount")}
          />
        )}
        {shape.quantity && (
          <DecimalInput
            label={shape.quantityLabel}
            value={quantity}
            onChange={setQuantity}
            required
            placeholder="0"
            {...err("quantity")}
          />
        )}
        {shape.price && (
          <DecimalInput
            label={M.hareketler.form.birimFiyat}
            value={price}
            onChange={setPrice}
            required={shape.priceRequired}
            currency={currency}
            {...err("price")}
          />
        )}
        {type === "FX_CONVERSION" && (
          <>
            <Field label={M.hareketler.form.alinanParaBirimi} htmlFor="tx-alinan-pb" required {...err("alinanParaBirimi")}>
              <CurrencySelect
                id="tx-alinan-pb"
                className="pf-fig"
                value={alinanParaBirimi}
                onChange={setAlinanParaBirimi}
                invalid={errors.alinanParaBirimi !== undefined}
              />
            </Field>
            <DecimalInput
              label={M.hareketler.form.alinanTutar}
              value={alinanTutar}
              onChange={setAlinanTutar}
              required
              currency={alinanParaBirimi}
              {...err("alinanTutar")}
            />
          </>
        )}
        {shape.fee && (
          <DecimalInput label={M.hareketler.form.masraf} value={fee} onChange={setFee} currency={currency} {...err("fee")} />
        )}
        {shape.tax && (
          <DecimalInput label={M.hareketler.form.vergiStopaj} value={tax} onChange={setTax} currency={currency} {...err("tax")} />
        )}
        {shape.fxOverride && (
          <DecimalInput
            label={M.hareketler.form.kurElle}
            value={fx}
            onChange={setFx}
            hint={M.hareketler.form.kurElleIpucu}
            {...err("fx")}
          />
        )}
      </div>

      <div onChangeCapture={() => setAssetTouched(true)}>
        <AssetBindingField
          value={asset}
          onChange={setAsset}
          errors={{
            ...(errors.instrumentId === undefined ? {} : { instrumentId: errors.instrumentId }),
            ...(errors.label === undefined ? {} : { label: errors.label }),
            ...(errors.assetCurrency === undefined ? {} : { currency: errors.assetCurrency }),
          }}
          {...(shape.assetMode === "cash"
            ? { note: M.hareketler.form.nakitNotu }
            : {})}
        />
      </div>

      {type === "BUY" && (
        <label className="flex items-start gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={temettuden}
            onChange={(e) => setTemettuden(e.currentTarget.checked)}
          />
          <span>
            {M.hareketler.form.temettuden}
            <span className="block text-[12px] text-muted">
              {M.hareketler.form.temettudenMetin}
            </span>
          </span>
        </label>
      )}

      {type === "TAX_PAYMENT" && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label={M.hareketler.form.aitYil} htmlFor="tx-vergi-yili" required {...err("vergiYili")}>
            <TextInput
              id="tx-vergi-yili"
              inputMode="numeric"
              className="pf-fig"
              value={vergiYili}
              onChange={(e) => setVergiYili(e.currentTarget.value)}
              aria-invalid={errors.vergiYili !== undefined}
            />
          </Field>
          <fieldset className="grid gap-1 text-[13px] sm:col-span-2">
            <legend className="mb-1 text-[13px] font-medium text-ink">{M.hareketler.form.kaynak}</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="tx-vergi-kaynagi"
                checked={vergiKaynagi === "nakit"}
                onChange={() => setVergiKaynagi("nakit")}
              />
              {M.hareketler.form.kaynakNakit}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="tx-vergi-kaynagi"
                checked={vergiKaynagi === "disaridan"}
                onChange={() => setVergiKaynagi("disaridan")}
              />
              {M.hareketler.form.kaynakDisaridan}
            </label>
          </fieldset>
        </div>
      )}

      {type === "SELL" && transaction === null && (
        <label className="flex items-start gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={paraCekildi}
            onChange={(e) => setParaCekildi(e.currentTarget.checked)}
          />
          <span>
            {M.hareketler.form.paraCektim}
            <span className="block text-[12px] text-muted">
              {M.hareketler.form.paraCektimMetin}
            </span>
          </span>
        </label>
      )}

      <Field label={M.hareketler.form.not} htmlFor="tx-note">
        <TextArea id="tx-note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      </Field>

      {shape.amount && (
        <Notice tone="info" title={M.hareketler.form.nasilBaslik}>
          <p>
            {M.hareketler.form.nasilOnce}
            <code>Transaction</code>
            {M.hareketler.form.nasilTip}
            <code>{M.hareketler.form.nasilMiktar}</code>
            {M.hareketler.form.nasilVirgul}
            <code>{M.hareketler.form.nasilBirimFiyat}</code>
            {M.hareketler.form.nasilSonra}
          </p>
        </Notice>
      )}

      {kilit !== null && <KilitliKabiliyet ne={M.hareketler.form.varlikSiniri} aciklama={kilit} />}
      <InlineError error={save.error} />

      <div className="flex justify-end gap-2 border-t border-line pt-3">
        <Button onClick={onCancel}>{M.ortak.vazgec}</Button>
        <Button type="submit" variant="primary" disabled={save.pending || accounts.length === 0}>
          {save.pending ? M.ortak.kaydediliyor : M.ortak.kaydet}
        </Button>
      </div>
    </form>
  );
}
